import { buildSymbolDependentRequestHelpers } from 'widgets/streaming/buildSymbolDependentRequestHelpers';
import {
    CorporateActionsRequest,
    CorporateActionsRequestDateSearchType,
} from 'widgets/corpActions/api/CorporateActionsRequest';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import State from 'store/State';
import { StreamRequestInfo } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import {
    CorporateActionsWidgetConfigState,
    initialCorporateActionsConfiguration,
} from 'widgets/corpActions/widget/store/config/CorporateActionsWidgetConfigState';
import { CorporateActionType } from 'widgets/corpActions/data/CorporateActionType';
import { getEnumKey } from '@instinet-analytics/core-lib';
import { WidgetId } from 'widgets/WidgetId';
import { selectWidgetInstanceConfig } from 'widgets/store/selectors';
import {
    SymbolEventsRequest,
    SymbolEventsTypeValue,
} from 'widgets/corpActions/api/SymbolEventsRequest';
import {
    CorpActionTypeHolidayEnum,
    isCorpActionHolidayType,
} from 'widgets/corpActions/data/HolidayType';
import { DataServices } from 'store/Services';

function buildRequest(
    symbols: string | undefined,
    symbology: SymbologyValues,
    configState: CorporateActionsWidgetConfigState
): SymbolEventsRequest | undefined {
    if (!symbols) {
        return undefined;
    }

    const config = configState ?? initialCorporateActionsConfiguration;

    const { caTypes } = config;

    const request: SymbolEventsRequest = {
        symbols,
        symbology,
        dateSearchType: 'DEFAULT',
        limit: 0,
    };

    const dateStrategy = getDateStrategy(config);
    dateStrategy.setRequestParameters(request);

    if (caTypes && caTypes.length > 0) {
        request.types = caTypes
            .map((t) =>
                isCorpActionHolidayType(t)
                    ? getEnumKey(CorpActionTypeHolidayEnum, t)
                    : getEnumKey(CorporateActionType, t)
            )
            .join(',');
    }

    return request;
}

function toRequestUid(
    symbols: string,
    configState: CorporateActionsWidgetConfigState | undefined
): string {
    const config = configState ?? initialCorporateActionsConfiguration;
    const { caTypes } = config;

    let ret = `symbolEvents-${symbols}`;

    const dateStrategy = getDateStrategy(config);
    ret += dateStrategy.requestUidSuffix;

    if (caTypes && caTypes.length > 0) {
        ret += '/' + caTypes.sort().join(',');
    }
    return ret;
}

export const symbolEventsRequestToStreamKey = (r: SymbolEventsRequest) => {
    const config: CorporateActionsWidgetConfigState = {
        ...initialCorporateActionsConfiguration,
        ...exclusionsFromDateSearchType(r.dateSearchType),
        caTypes:
            r.types && r.types.length > 0
                ? (r.types!.split(',') as Array<SymbolEventsTypeValue>)
                : [],
    };
    return toRequestUid(r.symbols, config);
};

type SingleSymbolCorporateActionsWidgetConfigState =
    CorporateActionsWidgetConfigState & { symbol?: string };

const singleSymbolHelpers = buildSymbolDependentRequestHelpers<
    SymbolEventsRequest,
    SingleSymbolCorporateActionsWidgetConfigState
>(
    toRequestUid,
    symbolEventsRequestToStreamKey,
    buildRequest,
    initialCorporateActionsConfiguration,
    (r) => r.symbols
);

const getRequestUidForSymbol = (
    symbol: string,
    config: CorporateActionsWidgetConfigState
) => toRequestUid(symbol, config);

const {
    buildStreamRequest: buildSingleSymbolStreamRequest,
    getStreamRequestInfosForSymbols,
} = singleSymbolHelpers;

async function buildStreamRequest(
    state: State,
    services: DataServices,
    id: WidgetId
) {
    // This will return a request if we have a symbol in config or the selection is a single symbol...
    const singleSymbolRequest = await buildSingleSymbolStreamRequest(
        state,
        services,
        id
    );
    if (singleSymbolRequest) {
        return singleSymbolRequest;
    }

    // We also support multiple symbols in the portfolio...
    const selection = selectPortfolioSelection(state);
    const selectionWrapper = new PortfolioSelectionWrapper(selection);
    const selectedSymbols = selectionWrapper.selectedSymbols.filter((s) => !!s);
    const config =
        selectWidgetInstanceConfig(state, id) ??
        initialCorporateActionsConfiguration;
    if (selection && selectedSymbols.length > 0) {
        const symbols = selectedSymbols.sort().join(',');
        return buildRequest(symbols, selection.symbology, config)!;
    }
    return undefined;
}

async function getWidgetStreamRequestInfo(
    state: State,
    services: DataServices,
    id: WidgetId
): Promise<StreamRequestInfo<SymbolEventsRequest> | undefined> {
    const request = await buildStreamRequest(state, services, id);
    const config =
        selectWidgetInstanceConfig(state, id) ??
        initialCorporateActionsConfiguration;
    if (!request) {
        return undefined;
    }

    return request
        ? {
              streamKey: toRequestUid(request.symbols, config),
              payload: request,
          }
        : undefined;
}

async function createWidgetRequestUid(
    state: State,
    services: DataServices,
    id: WidgetId
) {
    const request = await buildStreamRequest(state, services, id);
    if (!request) {
        return undefined;
    }
    return symbolEventsRequestToStreamKey(request);
}

export {
    getWidgetStreamRequestInfo as getSymbolEventsStreamRequestInfo,
    getStreamRequestInfosForSymbols as getSymbolEventsStreamRequestInfosForSymbols,
    createWidgetRequestUid as createSymbolEventsWidgetRequestUid,
    getRequestUidForSymbol,
};

export interface CorporateActionsWidgetDateStrategy {
    setRequestParameters(request: CorporateActionsRequest): void;
    requestUidSuffix: string;
}

const dateStrategyHistorical: CorporateActionsWidgetDateStrategy = {
    setRequestParameters: (r) => {
        r.dateSearchType = 'HISTORICAL';
    },
    requestUidSuffix: '/HISTORICAL',
};
const dateStrategyUpcoming: CorporateActionsWidgetDateStrategy = {
    setRequestParameters: (r) => {
        r.dateSearchType = 'UPCOMING';
    },
    requestUidSuffix: '/UPCOMING',
};
const dateStrategyTodayOnly: CorporateActionsWidgetDateStrategy = {
    setRequestParameters: (r) => {
        r.dateSearchType = 'WINDOW';
        r.lookbackPeriod = 'P0D';
        r.lookforwardPeriod = 'P0D';
    },
    requestUidSuffix: '/TODAY',
};
const dateStrategyDefault: CorporateActionsWidgetDateStrategy = {
    setRequestParameters: (r) => {
        r.dateSearchType = 'DEFAULT';
    },
    requestUidSuffix: '',
};

function exclusionsFromDateSearchType(
    dateSearchType: CorporateActionsRequestDateSearchType
) {
    return {
        excludeUpcoming:
            dateSearchType === 'HISTORICAL' || dateSearchType === 'WINDOW',
        excludeHistorical:
            dateSearchType === 'UPCOMING' || dateSearchType === 'WINDOW',
        excludeToday: dateSearchType !== 'WINDOW',
    };
}

function getDateStrategy(
    config: CorporateActionsWidgetConfigState
): CorporateActionsWidgetDateStrategy {
    const { excludeUpcoming, excludeHistorical, excludeToday } = config;

    if (excludeUpcoming && !excludeHistorical) {
        return dateStrategyHistorical;
    } else if (excludeHistorical && !excludeUpcoming) {
        return dateStrategyUpcoming;
    } else if (!excludeToday && excludeHistorical && excludeUpcoming) {
        return dateStrategyTodayOnly;
    } else {
        return dateStrategyDefault;
    }
}
